import { useMemo, useState } from 'react';
import modalStyles from './Modal.module.css';
import styles from './QuotaEditModal.module.css';

function groupQuotasByType(quotas) {
  const groups = new Map();

  (quotas ?? []).forEach((quota) => {
    const type = quota.cnq_type;
    if (!groups.has(type)) groups.set(type, []);
    groups.get(type).push(quota);
  });

  return [...groups.entries()].sort(([a], [b]) => Number(a) - Number(b));
}

function getTerminationCnqId(user) {
  const termination = user?.user_termination;
  if (!termination || termination.cnq_id == null) return null;
  return Number(termination.cnq_id);
}

function isOverQuotaQuota(quota, terminationCnqId) {
  if (!quota) return false;
  if (quota.is_over_quota_reason === true) return true;
  if (terminationCnqId == null || quota.cnq_id == null) return false;
  return Number(quota.cnq_id) === terminationCnqId;
}

function getOrphanTerminationQuota(user, terminationCnqId) {
  if (terminationCnqId == null) return null;

  const inEditable = (user.editable_quotas ?? []).some(
    (quota) => Number(quota.cnq_id) === terminationCnqId,
  );
  if (inEditable) return null;

  const orphan = user?.user_termination?.quota;
  if (!orphan || typeof orphan !== 'object') {
    return { cnq_id: terminationCnqId, quota_name: null, cnq_type: null };
  }

  return {
    cnq_id: orphan.cnq_id ?? terminationCnqId,
    quota_name: orphan.quota_name ?? null,
    cnq_type: orphan.cnq_type ?? null,
    cnq_audience: orphan.cnq_audience,
    cnq_reach: orphan.cnq_reach,
    is_full: true,
    is_over_quota_reason: true,
  };
}

function QuotaCheckboxRow({ quota, checked, disabled, onToggle, isOverQuota }) {
  const label = quota.quota_name?.trim() || `Quota #${quota.cnq_id}`;

  return (
    <label
      className={`${styles.quotaRow} ${quota.is_full || isOverQuota ? styles.quotaRowFull : ''} ${isOverQuota ? styles.quotaRowOver : ''} ${disabled ? styles.quotaRowDisabled : ''}`}
    >
      <input
        type="checkbox"
        className={styles.quotaCheckbox}
        checked={checked}
        disabled={disabled}
        onChange={() => onToggle(quota)}
      />
      <div className={styles.quotaContent}>
        <div className={styles.quotaHeader}>
          <span className={styles.quotaName}>{label}</span>
          {quota.cnq_type != null && (
            <span className={styles.typeBadge}>Type {quota.cnq_type}</span>
          )}
          {(quota.is_full || isOverQuota) && <span className={styles.fullBadge}>Full</span>}
          {isOverQuota && <span className={styles.overReasonBadge}>Over-quota reason</span>}
        </div>
        <div className={styles.quotaMeta}>
          {(quota.cnq_audience != null || quota.cnq_reach != null) && (
            <span className={styles.reachCount}>
              {quota.cnq_audience ?? 0} / {quota.cnq_reach ?? 0}
            </span>
          )}
          <span className={styles.quotaId}>cnq_id: {quota.cnq_id}</span>
        </div>
      </div>
    </label>
  );
}

function OrphanTerminationQuota({ quota }) {
  const label = quota.quota_name?.trim() || `Quota #${quota.cnq_id}`;

  return (
    <div className={`${styles.quotaRow} ${styles.quotaRowFull} ${styles.quotaRowOver} ${styles.quotaRowOrphan}`}>
      <div className={styles.quotaContent}>
        <div className={styles.quotaHeader}>
          <span className={styles.quotaName}>{label}</span>
          {quota.cnq_type != null && (
            <span className={styles.typeBadge}>Type {quota.cnq_type}</span>
          )}
          <span className={styles.fullBadge}>Full</span>
          <span className={styles.overReasonBadge}>Previous study</span>
        </div>
        <div className={styles.quotaMeta}>
          {(quota.cnq_audience != null || quota.cnq_reach != null) && (
            <span className={styles.reachCount}>
              {quota.cnq_audience ?? 0} / {quota.cnq_reach ?? 0}
            </span>
          )}
          <span className={styles.quotaId}>cnq_id: {quota.cnq_id}</span>
        </div>
        <p className={styles.orphanHint}>
          This quota is not in the current editable list (older / previous study).
        </p>
      </div>
    </div>
  );
}

export default function QuotaEditModal({
  user,
  onClose,
  onSave,
  onSaveAndRevive,
  canSave = true,
  saveButtonLabel = 'Save & Revive',
  saveDisabledHint = null,
}) {
  const handleSave = onSave ?? onSaveAndRevive;
  const initialIds =
    user.selected_cnq_ids ??
    (user.editable_quotas ?? []).filter((quota) => quota.is_selected).map((quota) => quota.cnq_id);
  const [selectedIds, setSelectedIds] = useState(() => [...initialIds]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const termination = user?.user_termination ?? null;
  const terminationCnqId = getTerminationCnqId(user);
  const terminationReason =
    termination?.reason != null && String(termination.reason).trim()
      ? String(termination.reason).trim()
      : null;
  const orphanQuota = useMemo(
    () => getOrphanTerminationQuota(user, terminationCnqId),
    [user, terminationCnqId],
  );

  const groupedQuotas = useMemo(
    () => groupQuotasByType(user.editable_quotas ?? []),
    [user.editable_quotas],
  );
  const editableQuotaCount = user.editable_quotas?.length ?? 0;

  const addedIds = selectedIds.filter((id) => !initialIds.includes(id));
  const removedIds = initialIds.filter((id) => !selectedIds.includes(id));
  const hasChanges = addedIds.length > 0 || removedIds.length > 0;

  const toggleQuota = (quota) => {
    const isChecked = selectedIds.includes(quota.cnq_id);
    if (quota.is_full && !isChecked) return;

    setSelectedIds((prev) =>
      isChecked ? prev.filter((id) => id !== quota.cnq_id) : [...prev, quota.cnq_id],
    );
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError('');
    try {
      await handleSave({
        wh_mo_id: user.wh_mo_id,
        cnt_id: user.cnt_id,
        selected_cnq_ids: selectedIds,
      });
    } catch (e) {
      setError(e.message || 'Failed to re-assign and revive user.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={modalStyles.overlay} onClick={onClose}>
      <div
        className={modalStyles.panelWide}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="quota-reassign-title"
      >
        <div className={modalStyles.header}>
          <h2 id="quota-reassign-title" className={modalStyles.title}>
            Re-assign Quota
          </h2>
          <div className={modalStyles.idPill}>
            <span className={modalStyles.idLabel}>wh_mo_id</span>
            <span className={modalStyles.idValue}>{user.wh_mo_id}</span>
          </div>
          <div className={modalStyles.idPill}>
            <span className={modalStyles.idLabel}>cnt_id</span>
            <span className={modalStyles.idValue}>{user.cnt_id}</span>
          </div>
          <button type="button" className={modalStyles.closeBtn} onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className={`${modalStyles.body} ${styles.body}`}>
          {terminationReason && (
            <div className={styles.terminationBanner}>
              <p className={styles.terminationLabel}>Termination reason</p>
              <p className={styles.terminationText}>{terminationReason}</p>
            </div>
          )}

          {orphanQuota && (
            <section className={styles.group}>
              <h3 className={styles.groupTitle}>Over-quota (previous study)</h3>
              <OrphanTerminationQuota quota={orphanQuota} />
            </section>
          )}

          {editableQuotaCount === 0 ? (
            <div className={styles.emptyState}>No editable quotas available for cnt_id {user.cnt_id}.</div>
          ) : (
            groupedQuotas.map(([type, quotas]) => (
              <section key={type} className={styles.group}>
                <h3 className={styles.groupTitle}>cnq_type {type}</h3>
                <div className={styles.groupList}>
                  {quotas.map((quota) => {
                    const checked = selectedIds.includes(quota.cnq_id);
                    const disabled = quota.is_full && !checked;
                    const isOver = isOverQuotaQuota(quota, terminationCnqId);

                    return (
                      <QuotaCheckboxRow
                        key={quota.cnq_id}
                        quota={quota}
                        checked={checked}
                        disabled={disabled}
                        isOverQuota={isOver}
                        onToggle={toggleQuota}
                      />
                    );
                  })}
                </div>
              </section>
            ))
          )}

          {hasChanges && (
            <div className={styles.changeSummary}>
              <p className={styles.changeTitle}>Pending changes</p>
              {addedIds.length > 0 && (
                <p className={styles.changeAdded}>
                  Added: {addedIds.join(', ')}
                </p>
              )}
              {removedIds.length > 0 && (
                <p className={styles.changeRemoved}>
                  Removed: {removedIds.join(', ')}
                </p>
              )}
            </div>
          )}

          {saveDisabledHint && !canSave && (
            <p className={styles.saveHint}>{saveDisabledHint}</p>
          )}

          {error && <div className={modalStyles.inlineError}>{error}</div>}
        </div>

        <div className={modalStyles.footer}>
          <button type="button" className={modalStyles.cancelBtn} onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            type="button"
            className={styles.saveReviveBtn}
            onClick={handleSubmit}
            disabled={saving || !canSave || editableQuotaCount === 0 || selectedIds.length === 0}
          >
            {saving ? 'Saving…' : saveButtonLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
